import "./Input.css";

import { onlyNumbersProps } from "@gymflow/common";
import { cn } from "@gymflow/helpers";
import { cva, VariantProps } from "class-variance-authority";
import React, {
  FocusEventHandler,
  forwardRef,
  HTMLInputTypeAttribute,
  ReactNode,
  useState,
} from "react";

import { Spinner } from "../../Spinner";
import Label from "../Label";

type InputVariant = VariantProps<typeof inputVariants>["intent"];

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onKeyPress" | "onPaste" // because of "...onlyNumbersProps" in <input />
> & {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  label?: string | ReactNode;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  readOnly?: boolean;
  renderOverInput?: ReactNode;
  variant?: InputVariant;
  disabled?: boolean;
  isRequired?: boolean;
  type?: HTMLInputTypeAttribute;
  helpTooltip?: string;
  isOptional?: boolean;
  button?: {
    label: string;
    onClick: () => Promise<void> | void;
    disabled?: boolean;
    loading?: boolean;
  };
};

const inputVariants = cva(
  "shadow-xs outline-secondary-200 dark:ring-secondary-600 w-full rounded-[0.625rem] border border-gray-200 px-3.5 text-base font-medium disabled:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:!outline-none focus:dark:ring-1",
  {
    variants: {
      intent: {
        md: "h-10 py-2.5",
        sm: "h-9 py-1.5",
        unstyled:
          "rounded-none border-none border-transparent bg-transparent p-0 shadow-none outline-none outline-transparent disabled:bg-transparent dark:border-transparent dark:bg-transparent dark:ring-transparent focus:dark:ring-0",
      },
      defaultVariants: {
        intent: "md",
      },
    },
  },
);

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      value,
      startIcon,
      labelClassName,
      helpTooltip,
      endIcon,
      renderOverInput,
      variant = "md",
      isRequired = false,
      disabled,
      isOptional,
      button,
      ...rest
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    return (
      <div className="relative flex w-full flex-col gap-1.5">
        <Label
          className={labelClassName}
          label={label}
          isRequired={isRequired}
          helpTooltip={helpTooltip}
          isOptional={isOptional}
        />
        <div className="relative flex flex-row">
          <div className="relative flex-1">
            {startIcon && (
              <div className="absolute left-3.5 top-1/2 z-10 -translate-y-1/2">
                {startIcon}
              </div>
            )}
            <input
              {...rest}
              ref={ref}
              placeholder={rest.placeholder}
              value={value}
              disabled={disabled}
              onFocus={(e) => {
                setIsFocused(true);
                rest.onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                rest.onBlur?.(e);
              }}
              className={cn(
                inputVariants({ intent: variant }),
                {
                  "text-gray-950 dark:text-gray-0": value,
                  "text-gray-500": !value,
                  "pl-10": startIcon,
                  "pr-10": endIcon,
                  "text-ellipsis overflow-hidden whitespace-nowrap": !isFocused,
                  "cursor-not-allowed opacity-50": disabled,
                  "rounded-r-none": button,
                },
                className,
              )}
              {...(rest.type === "number" && onlyNumbersProps)}
            />
            {endIcon && (
              <div className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2">
                {endIcon}
              </div>
            )}
          </div>
          {button && (
            <div
              className={cn(
                "flex cursor-pointer items-center rounded-[0.625rem] rounded-l-none border border-l-0 border-gray-200 px-3 bg-gray-0 dark:bg-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-950",
                {
                  "cursor-not-allowed opacity-50": button.disabled,
                },
              )}
              onClick={async () => {
                const result = button.onClick();
                if (result instanceof Promise) {
                  setIsLoading(true);
                  try {
                    await result;
                  } catch {
                    setIsLoading(false);
                  }
                  setIsLoading(false);
                }
              }}
            >
              {isLoading || button.loading ? (
                <Spinner />
              ) : (
                <div className="dark:text-gray-25 text-sm font-semibold text-gray-950">
                  {button.label}
                </div>
              )}
            </div>
          )}
          {renderOverInput}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";
